import { formatDateCustom } from '@/utils/date';

/** Where a day's tick sits along the track, in percent. Days are evenly spaced, one per step. */
export function tickPercent(index: number, count: number): number {
    return count <= 1 ? 50 : (index / (count - 1)) * 100;
}

/** The day nearest a point along the track, given as a fraction of its width. */
export function indexAtFraction(fraction: number, count: number): number {
    if (count <= 1) {
        return 0;
    }
    const clamped = Math.min(1, Math.max(0, fraction));
    return Math.round(clamped * (count - 1));
}

/** The width a date label needs, with room around it, at the scrubber's 12px type. */
export function labelWidthPx(withYear: boolean): number {
    return withYear ? 64 : 44;
}

const LABEL_GAP_PX = 8;

/**
 * Which days get a date under the track: the first and the last, plus as many evenly spaced
 * ones between as fit without touching. The end labels are aligned to the track's ends and
 * the others centred on their tick, which is what the spacing check measures against.
 *
 * Without a measured width (before layout, or in jsdom) only the two ends are labelled.
 */
export function labelIndices(count: number, widthPx: number | null, labelPx: number): number[] {
    if (count === 0) {
        return [];
    }
    if (count === 1) {
        return [0];
    }
    if (widthPx === null || widthPx <= 0) {
        return [0, count - 1];
    }
    if (widthPx < 2 * labelPx + LABEL_GAP_PX) {
        return [0];
    }
    for (
        let candidate = Math.min(count, Math.floor(widthPx / labelPx) + 1);
        candidate > 2;
        candidate--
    ) {
        const indices = spread(count, candidate);
        if (labelsClear(indices, count, widthPx, labelPx)) {
            return indices;
        }
    }
    return [0, count - 1];
}

/** `labels` day indices, evenly spaced from the first day to the last. */
function spread(count: number, labels: number): number[] {
    return Array.from({ length: labels }, (_, k) => Math.round((k * (count - 1)) / (labels - 1)));
}

/** Whether labels at these days leave a gap between each pair, as they are aligned on screen. */
function labelsClear(indices: number[], count: number, widthPx: number, labelPx: number): boolean {
    const last = indices.length - 1;
    const spans = indices.map((index, position) => {
        const x = (index / (count - 1)) * widthPx;
        if (position === 0) {
            return [0, labelPx];
        }
        if (position === last) {
            return [widthPx - labelPx, widthPx];
        }
        return [x - labelPx / 2, x + labelPx / 2];
    });
    return spans.every((span, k) => k === 0 || span[0] - spans[k - 1][1] >= LABEL_GAP_PX);
}

/** Whether the days run over more than one calendar year, so the labels need the year. */
export function spansYears(days: { day: string }[]): boolean {
    return days.length > 1 && days[0].day.slice(0, 4) !== days[days.length - 1].day.slice(0, 4);
}

/** A short label for a day (`YYYY-MM-DD`), in the portal's day-first order. */
export function shortDayLabel(day: string, withYear: boolean): string {
    return formatDateCustom(day, withYear ? 'dd/MM/yy' : 'dd/MM');
}
